import React, { useCallback, useEffect } from "react";
import { View } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { Input, Modal } from "@edgelogistics/edge-ui/lib/components";
import { useNotification } from "@edgelogistics/edge-ui/lib/providers/notification";
import { UserSessionI } from "../../../../redux/auth/authTypes";
import { updateInformation } from "../../../../redux/profile/profileActions";
import { updateUserSelector } from "../../../../redux/profile/profileSelector";
import { styles } from "./styles";
import { useFormik } from "formik";
import { usePlatforms } from "../../../../hooks/usePlatforms";
import TimeZoneSearchField from "../../../../components/SearchFields/TimeZoneSearchField";
import { UserPayloadI } from "../../../../redux/profile/profileTypes";
import { profileValidationSchema } from "../../../../config/validations/profile";
import { useQueryClient } from '@tanstack/react-query';
import { capacityLogsKeys } from "../../../../providers/approvals/hooks/useCapacityLogs";
import { approvalsKeys } from "../../../../providers/approvals/hooks/useApprovalsLogs";

export interface EditUserProps {
  open: boolean;
  handleClose: () => void;
  user: UserSessionI;
}

const EditUser: React.FC<EditUserProps> = ({ open, handleClose, user }: EditUserProps) => {
  const dispatch = useDispatch();
  const { loading } = useSelector(updateUserSelector);
  const { useMinMediaQuery } = usePlatforms();
  const isTablet = useMinMediaQuery(769);
  const queryClient = useQueryClient();
  const { setNotification } = useNotification();
  const handleNotification = setNotification;

  const onSuccess = () => {
    handleClose();
    queryClient.invalidateQueries(capacityLogsKeys.all);
    queryClient.invalidateQueries(approvalsKeys.all);
    handleNotification({ message: "Profile information was updated successfully", type: "success" });
  };
  const onFailure = (error: string) => {
    handleNotification({ message: error, type: "error" });
  };

  const handleEditUser = useCallback(
    ({ timezone }) => {
      const payload: UserPayloadI = {
        timezone_id: timezone?.timezone_id,
      };

      dispatch(updateInformation(payload, onSuccess, onFailure));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ dispatch ],
  );

  const {
    setValues, resetForm, setFieldValue, values, errors, touched, handleSubmit,
  } = useFormik({
    validationSchema : profileValidationSchema,
    initialValues    : user,
    onSubmit         : handleEditUser,
  });

  useEffect(() => {
    resetForm();
    setValues(user);
  }, [ setValues, resetForm, open, user ]);

  return (
    <Modal
      open={open}
      handleClose={handleClose}
      onCancel={handleClose}
      onConfirm={handleSubmit}
      title="Update User Information"
      confirmText="Accept"
      loadingConfirm={loading}
      testID="editUser"
    >
      <View style={styles.container}>
        <View style={isTablet && styles.inputContainer}>
          <View style={isTablet ? [ styles.largeDevicesInput, { marginRight: 10 } ] : styles.input}>
            <Input
              leftIcon="User"
              placeholder="Name"
              value={values?.name ?? ""}
              name="name"
              onChange={setFieldValue}
              error={errors.name ? String(errors.name) : ""}
              testID="editUserNameInput"
              disabled
            />
          </View>
          <View style={isTablet ? [ styles.largeDevicesInput, { marginLeft: 10 } ] : styles.input}>
            <Input
              leftIcon="Email"
              placeholder="Email"
              value={values?.email ?? ""}
              name="email"
              onChange={() => null}
              disabled
            />
          </View>
        </View>
        <View style={isTablet && styles.inputContainer}>
          <View style={isTablet ? [ styles.largeDevicesInput, { width: "198px" } ] : styles.input}>
            <TimeZoneSearchField
              selectedValue={values.timezone?.timezone_id ? values.timezone : {}}
              onChangeValue={setFieldValue}
              placeholder="Timezone"
              name="timezone"
              nameProp="name"
              error={touched.timezone && errors.timezone}
              clear={() => setFieldValue("timezone", null)}
              testID="editUserTimeZoneField"
            />
          </View>
          <View style={isTablet ? [ styles.largeDevicesInput, { marginLeft: 20 } ] : styles.input}>
            <Input
              leftIcon="UserGroup"
              placeholder="Dispatch Division"
              value={values?.dispatch_division?.country ?? ""}
              name="email"
              onChange={() => null}
              disabled
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default EditUser;
